import React, { useCallback, useEffect, useState } from "react";
import Slider from "react-slick";
import {
  Box,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Fab,
  Tooltip,
  Stack,
  Popover,
} from "@mui/material";
import {
  FaLanguage,
  FaHeading,
  FaComment,
  FaImages,
  FaMousePointer,
  FaEye,
  FaPlus,
  FaTrash,
  FaEdit,
  FaArrowLeft,
  FaCog,
  FaList,
  FaExpandArrowsAlt,
  FaCompressArrowsAlt,
  FaExpand,
} from "react-icons/fa";
import AddIcon from "@mui/icons-material/Add";
import { LanguageAutocomplete } from "../widgets/LanguageAutocomplete";
import PageTitle from "../widgets/PageTitle";

// Import slick carousel styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import TextFieldVariable from "../components/TextFieldVariable";
import BackBtn from "../widgets/BackBtn";
import RcsMediaSelector from "./RcsMediaSelector";
import { useDialog } from "../context/DialogContext";
import { getHeader, toastError } from "../utils/util";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { MdAdd, MdOutlineArrowCircleDown } from "react-icons/md";
import { ArrowDropDown } from "@mui/icons-material";
import { GlassButton, GlassButtonLoading } from "../themes/UiElements";
import RcsImageCropper from "../components/rcs/RcsImageCropper";
import { MdImage, MdVideoLibrary, MdPictureAsPdf } from "react-icons/md";
// Styled Paper component for sections
const SectionPaper = ({ children, title, icon, defaultExpanded = true }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    sx={{
      mb: 2,
      "&:before": { display: "none" },
      boxShadow: 2,
      borderRadius: 2,
      overflow: "hidden",
    }}
  >
    <AccordionSummary expandIcon={<ArrowDropDown />}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography variant="h6" fontWeight="600">
          {title}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 1 }}>{children}</AccordionDetails>
  </Accordion>
);

// Enhanced Suggestions Editor Component
const SuggestionsEditor = ({
  suggestions,
  onChange,
  maxSuggestions = 2,
  title = "Suggestions",
}) => {
  const theme = useTheme();

  const handleSuggestionChange = (sugIndex, field, value) => {
    const newSuggestions = [...suggestions];
    if (field === "type") {
      const currentType = newSuggestions[sugIndex].type;
      if (currentType === value) return;
      if (!canSelectType(value, sugIndex)) return;
      delete newSuggestions[sugIndex].phoneNumber;
      delete newSuggestions[sugIndex].url;
    }
    newSuggestions[sugIndex][field] = value;
    onChange(newSuggestions);
  };

  const canSelectType = (typeOption, sugIndex) => {
    const currentType = suggestions[sugIndex].type;
    if (currentType === typeOption) return true;
    const maxForType =
      { dialerAction: 1, openUrl: 1, reply: 2 }[typeOption] || 99;
    const countExcludingCurrent = suggestions.filter(
      (b, i) => i !== sugIndex && b.type === typeOption
    ).length;
    return countExcludingCurrent < maxForType;
  };

  const addNewSuggestion = () => {
    onChange([...suggestions, { type: "reply", plainText: "", data: "" }]);
  };

  const removeSuggestion = (sugIndex) => {
    const newSuggestions = [...suggestions];
    newSuggestions.splice(sugIndex, 1);
    onChange(newSuggestions);
  };

  const getTypeColor = (type) => {
    const colors = {
      reply: theme.palette.primary.main,
      dialerAction: theme.palette.error.main,
      openUrl: theme.palette.success.main,
    };
    return colors[type] || theme.palette.grey[500];
  };

  const getTypeIcon = (type) => {
    const icons = {
      reply: <FaComment size={14} />,
      dialerAction: <FaHeading size={14} />,
      openUrl: <FaMousePointer size={14} />,
    };
    return icons[type] || <FaCog size={14} />;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight="600"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FaMousePointer /> {title} ({suggestions.length}/{maxSuggestions})
        </Typography>
        {suggestions.length < maxSuggestions && (
          <GlassButton
            variant="outlined"
            startIcon={<MdAdd />}
            onClick={addNewSuggestion}
            size="small"
          >
            Add Suggestion
          </GlassButton>
        )}
      </Box>

      {suggestions.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No suggestions added yet. Add suggestions to provide interactive
          options.
        </Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {suggestions.map((sug, sugIndex) => (
            <Paper
              key={sugIndex}
              sx={{ p: 2, borderLeft: `4px solid ${getTypeColor(sug.type)}` }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Chip
                  label={sug.type}
                  size="small"
                  icon={getTypeIcon(sug.type)}
                  sx={{
                    bgcolor: getTypeColor(sug.type),
                    color: "white",
                    textTransform: "capitalize",
                  }}
                />
                <Tooltip title="Remove suggestion">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeSuggestion(sugIndex)}
                  >
                    <FaTrash />
                  </IconButton>
                </Tooltip>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={sug.type}
                      onChange={(e) =>
                        handleSuggestionChange(sugIndex, "type", e.target.value)
                      }
                    >
                      <MenuItem
                        value="reply"
                        disabled={!canSelectType("reply", sugIndex)}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FaComment /> Reply
                        </Box>
                      </MenuItem>
                      <MenuItem
                        value="dialerAction"
                        disabled={!canSelectType("dialerAction", sugIndex)}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FaHeading /> Dialer Action
                        </Box>
                      </MenuItem>
                      <MenuItem
                        value="openUrl"
                        disabled={!canSelectType("openUrl", sugIndex)}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <FaMousePointer /> Open URL
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Display Text"
                    value={sug.plainText}
                    onChange={(e) =>
                      handleSuggestionChange(
                        sugIndex,
                        "plainText",
                        e.target.value
                      )
                    }
                    placeholder="Text shown to the user"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="PostBack Data"
                    value={sug.data}
                    onChange={(e) =>
                      handleSuggestionChange(sugIndex, "data", e.target.value)
                    }
                    placeholder="Data sent when clicked"
                    helperText="This data will be sent to your webhook"
                  />
                </Grid>
                {sug.type === "dialerAction" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      value={sug.phoneNumber || ""}
                      onChange={(e) =>
                        handleSuggestionChange(
                          sugIndex,
                          "phoneNumber",
                          e.target.value
                        )
                      }
                      placeholder="+1234567890"
                    />
                  </Grid>
                )}
                {sug.type === "openUrl" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="URL"
                      value={sug.url || ""}
                      onChange={(e) =>
                        handleSuggestionChange(sugIndex, "url", e.target.value)
                      }
                      placeholder="https://example.com"
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Enhanced Card Editor Component
const CardEditor = ({
  index,
  templateData,
  setTemplateData,
  richCardType,
  onRemove,
}) => {
  const card = templateData.cards[index];
  const { showMediaDialog } = useDialog();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleMediaTypeSelect = (mediaType) => {
    handlePopoverClose();
    showMediaDialog(
      `Select ${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}`,
      `Please select the ${mediaType} you want to use`,
      {
        isRcs: true,
        mediaType,
        selection: "single",
        mediaHeight: card.media.height,
        selectionLimit: 1,
        onConfirm: async (mode) => {
          handleMediaChange("fileUrl", mode[0].previewUrl);
        },
      }
    );
  };

  const open = Boolean(anchorEl);

  if (!card) {
    return null; // Prevent rendering if card is undefined
  }

  const handleCardChange = (field, value) => {
    const newCards = [...templateData.cards];
    newCards[index][field] = value;
    setTemplateData((prev) => ({ ...prev, cards: newCards }));
  };

  const handleMediaChange = (field, value) => {
    const newCards = [...templateData.cards];
    newCards[index].media[field] = value;
    setTemplateData((prev) => ({ ...prev, cards: newCards }));
  };

  const handleSuggestionsChange = (newSuggestions) => {
    const newCards = [...templateData.cards];
    newCards[index].suggestions = newSuggestions;
    setTemplateData((prev) => ({ ...prev, cards: newCards }));
  };

  return (
    <Paper sx={{ p: 3, mb: 2, border: 1, borderColor: "divider" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          color="primary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <FaImages /> Card {index + 1}
        </Typography>
        {richCardType === "carousel" &&
          templateData.cards.length > 1 &&
          onRemove && (
            <Tooltip title="Remove card">
              <IconButton
                color="error"
                onClick={() => onRemove(index)}
                size="small"
              >
                <FaTrash />
              </IconButton>
            </Tooltip>
          )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Card Title"
            value={card.title}
            onChange={(e) => handleCardChange("title", e.target.value)}
            placeholder="Enter card title"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Media Height</InputLabel>
            <Select
              label="Media Height"
              value={card.media.height}
              onChange={(e) => handleMediaChange("height", e.target.value)}
            >
              <MenuItem value="SHORT">Short</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="TALL">Tall</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            size="small"
            label="Card Description"
            value={card.description}
            onChange={(e) => handleCardChange("description", e.target.value)}
            placeholder="Enter card description"
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction={"row"} gap={2}>
            <TextField
              fullWidth
              size="small"
              label="Media URL"
              value={card.media.fileUrl}
              onChange={(e) => handleMediaChange("fileUrl", e.target.value)}
              placeholder="https://example.com/image.jpg"
              helperText="URL to the image or video file"
            />
            <Box>
              <GlassButton
                sx={{ minWidth: 164 }}
                onClick={handleButtonClick}
                startIcon={<AddIcon />}
              >
                Media Select
              </GlassButton>

              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 150 }}>
                  <MenuItem onClick={() => handleMediaTypeSelect("image")}>
                    <MdImage style={{ marginRight: 8 }} />
                    Image
                  </MenuItem>
                  <MenuItem onClick={() => handleMediaTypeSelect("video")}>
                    <MdVideoLibrary style={{ marginRight: 8 }} />
                    Video
                  </MenuItem>
                  <MenuItem onClick={() => handleMediaTypeSelect("pdf")}>
                    <MdPictureAsPdf style={{ marginRight: 8 }} />
                    PDF
                  </MenuItem>
                </Box>
              </Popover>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={3}>
        <SuggestionsEditor
          suggestions={card.suggestions}
          onChange={handleSuggestionsChange}
          maxSuggestions={richCardType === "standalone" ? 4 : 2}
          title="Card Suggestions"
        />
      </Box>
    </Paper>
  );
};

// Preview Component
const TemplatePreview = ({ templateData }) => {
  const theme = useTheme();

  let previewSuggestions = [];
  if (!templateData.richCardType) {
    previewSuggestions = templateData.buttons;
  } else if (templateData.richCardType === "carousel") {
    previewSuggestions = templateData.outerSuggestions;
  }

  return (
    <Paper sx={{ p: 3, position: "sticky", top: 20 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <FaEye /> Preview
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {templateData.messageBody && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Message:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
            <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
              {templateData.messageBody}
            </Typography>
          </Paper>
        </Box>
      )}

      {templateData.richCardType && templateData.cards.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Rich Card ({templateData.cards.length} card
            {templateData.cards.length > 1 ? "s" : ""}):
          </Typography>
          {templateData.cards.slice(0, 3).map((card, index) => (
            <Card key={index} sx={{ mb: 1 }}>
              <CardContent>
                <Typography
                  variant="subtitle2"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <FaHeading size={12} /> {card.title || "No title"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    whiteSpace: "pre-line", // 👈 preserves newlines
                  }}
                >
                  <FaComment size={10} /> {card.description || "No description"}
                </Typography>
              </CardContent>
            </Card>
          ))}
          {templateData.cards.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{templateData.cards.length - 3} more cards
            </Typography>
          )}
        </Box>
      )}

      {previewSuggestions.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Suggestions:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {previewSuggestions.slice(0, 4).map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion.plainText || "Suggestion"}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
        >
          <FaCog size={10} /> Template: {templateData.templateName || "Unnamed"}
        </Typography>
      </Box>
    </Paper>
  );
};

// Custom Back Button with FA icon
const CustomBackBtn = () => (
  <Button
    startIcon={<FaArrowLeft />}
    onClick={() => window.history.back()}
    sx={{ mb: 2 }}
  >
    Back
  </Button>
);

// Main component
const RcsAddTemplate = () => {
  const [submitting, setSubmitting] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);
  const theme = useTheme();

  const [templateData, setTemplateData] = useState({
    templateName: "",
    language: "en",
    messageBody: "",
    cards: [],
    buttons: [],
    richCardType: "",
    orientation: "VERTICAL",
    width: "MEDIUM_WIDTH",
    outerSuggestions: [],
  });

  const handleInputChange = (field, value) => {
    setTemplateData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addNewCard = () => {
    setTemplateData((prev) => {
      const newCards = [
        ...prev.cards,
        {
          id: Date.now(),
          orientation: prev.orientation || "VERTICAL",
          title: "",
          description: "",
          media: { height: "MEDIUM", fileUrl: "" },
          suggestions: [],
        },
      ];
      setSelectedCardIndex(newCards.length - 1);
      return { ...prev, cards: newCards };
    });
  };

  const handleRemoveCard = useCallback(
    (index) => {
      setTemplateData((prev) => {
        const newCards = [...prev.cards];
        newCards.splice(index, 1);
        return { ...prev, cards: newCards };
      });
      if (index < selectedCardIndex) {
        setSelectedCardIndex((prev) => prev - 1);
      } else if (index === selectedCardIndex) {
        setSelectedCardIndex(0);
      }
    },
    [selectedCardIndex]
  );

  const handleOuterSuggestionsChange = (newSuggestions) => {
    handleInputChange("outerSuggestions", newSuggestions);
  };

  useEffect(() => {
    if (templateData.richCardType && templateData.cards.length === 0) {
      addNewCard();
    }
  }, [templateData.richCardType]);

  const handleRichCardToggle = (e) => {
    const checked = e.target.checked;
    if (checked) {
      setTemplateData((prev) => {
        const newData = { ...prev, buttons: [] };
        if (newData.cards.length === 0) {
          newData.cards = [
            {
              id: Date.now(),
              orientation: "VERTICAL",
              title: "",
              description: "",
              media: { height: "MEDIUM", fileUrl: "" },
              suggestions: [],
            },
          ];
        }
        newData.richCardType = "standalone";
        return newData;
      });
      setSelectedCardIndex(0);
    } else {
      handleInputChange("richCardType", "");
      setTemplateData((prev) => ({ ...prev, cards: [] }));
      setSelectedCardIndex(0);
    }
  };

  const handleSumitTemplate = useCallback(async () => {
    try {
      setSubmitting(true);
      if (!templateData.templateName) {
        toast.error("please enter template name");
        return;
      }
      if (!templateData.language) {
        toast.error("please select language");
        return;
      }
      const isRichCard = !!templateData.richCardType;
      const isCarousel = templateData.richCardType === "carousel";
      if (isRichCard && isCarousel && templateData.cards.length < 2) {
        toast.error("Carousel requires at least 2 cards");
        return;
      }
      if (isRichCard && !isCarousel && templateData.cards.length !== 1) {
        toast.error("Standalone requires exactly 1 card");
        return;
      }
      if (!isRichCard && templateData.cards.length > 0) {
        toast.error("No cards allowed for text message");
        return;
      }
      const bodyMessage = templateData.messageBody || "";
      const getSuggestionPayload = (sug) => ({
        type: sug.type,
        text: sug.plainText || "",
        postback: sug.data || "",
        ...(sug.type === "dialerAction" && {
          phoneNumber: sug.phoneNumber || "",
        }),
        ...(sug.type === "openUrl" && { url: sug.url || "" }),
      });
      const payload = {
        templateName: templateData.templateName,
        language: templateData.language,
        bodyMessage,
        isRichCard,
        isCarousel,
        cards: isRichCard
          ? templateData.cards.map((card) => ({
              orientation:
                card.orientation || templateData.orientation || "VERTICAL",
              title: card.title || "",
              description: card.description || "",
              mediaHeight: card.media.height || "MEDIUM",
              mediaUrl: card.media.fileUrl || "",
              suggestions: card.suggestions
                .map(getSuggestionPayload)
                .filter((s) => s.text),
            }))
          : [],
        outerSuggestions: (() => {
          let suggestions = [];
          if (!isRichCard) {
            suggestions = templateData.buttons;
          } else if (isCarousel) {
            suggestions = templateData.outerSuggestions;
          }
          return suggestions.map(getSuggestionPayload).filter((s) => s.text);
        })(),
      };
      const response = await axios.post(
        `${API_ENDPOINT}/rcs/add-template`,
        payload,
        getHeader()
      );
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  }, [templateData]);
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <CustomBackBtn />
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Box sx={{ mb: 4 }}>
          <PageTitle>Create RCS Template</PageTitle>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} lg={8}>
            <SectionPaper
              title="Template Basics"
              icon={<FaHeading color={theme.palette.primary.main} />}
              defaultExpanded={true}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Template Name"
                    value={templateData.templateName}
                    onChange={(e) =>
                      handleInputChange("templateName", e.target.value)
                    }
                    placeholder="Enter template name"
                    helperText="Unique identifier for your template"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LanguageAutocomplete
                    value={templateData.language}
                    onChange={(newV) => handleInputChange("language", newV)}
                  />
                </Grid>
              </Grid>
            </SectionPaper>

            <SectionPaper
              title="Text Message"
              icon={<FaComment color={theme.palette.primary.main} />}
              defaultExpanded={true}
            >
              <TextFieldVariable
                label="Message Body"
                disabled={!!templateData.richCardType}
                value={templateData.messageBody}
                onParamChange={(newV) => {}}
                onChange={(newV) => handleInputChange("messageBody", newV)}
                multiline
                rows={3}
              />
            </SectionPaper>

            <SectionPaper
              title="Use Rich Card"
              icon={<FaImages color={theme.palette.primary.main} />}
              defaultExpanded={false}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!templateData.richCardType}
                    onChange={handleRichCardToggle}
                  />
                }
                label="Enable Rich Card"
              />

              {!!templateData.richCardType && templateData.cards.length > 0 && (
                <Box sx={{ mt: 2, pl: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Card Type</InputLabel>
                        <Select
                          label="Card Type"
                          value={templateData.richCardType}
                          onChange={(e) => {
                            const newType = e.target.value;
                            handleInputChange("richCardType", newType);
                            if (
                              newType === "standalone" &&
                              templateData.cards.length > 1
                            ) {
                              setTemplateData((prev) => ({
                                ...prev,
                                cards: [prev.cards[0]],
                              }));
                              setSelectedCardIndex(0);
                            } else if (
                              newType === "carousel" &&
                              templateData.cards.length < 2
                            ) {
                              const newCards = [...templateData.cards];
                              while (newCards.length < 2) {
                                newCards.push({
                                  id: Date.now(),
                                  orientation: "VERTICAL",
                                  title: "",
                                  description: "",
                                  media: { height: "MEDIUM", fileUrl: "" },
                                  suggestions: [],
                                });
                              }
                              setTemplateData((prev) => ({
                                ...prev,
                                cards: newCards,
                              }));
                              setSelectedCardIndex(0);
                            }
                          }}
                        >
                          <MenuItem value="standalone">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FaImages /> Standalone Card
                            </Box>
                          </MenuItem>
                          <MenuItem value="carousel">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FaList /> Carousel
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {templateData.richCardType === "standalone" && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Orientation</InputLabel>
                          <Select
                            label="Orientation"
                            value={templateData.orientation}
                            onChange={(e) => {
                              const value = e.target.value;
                              const newCards = [...templateData.cards];
                              newCards.forEach((card) => {
                                card.orientation = value;
                              });
                              setTemplateData((prev) => ({
                                ...prev,
                                orientation: value,
                                cards: newCards,
                              }));
                            }}
                          >
                            <MenuItem value="HORIZONTAL">Horizontal</MenuItem>
                            <MenuItem value="VERTICAL">Vertical</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}

                    {templateData.richCardType === "carousel" && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Card Width</InputLabel>
                          <Select
                            label="Card Width"
                            value={templateData.width}
                            onChange={(e) =>
                              handleInputChange("width", e.target.value)
                            }
                          >
                            <MenuItem value="SMALL_WIDTH">Small</MenuItem>
                            <MenuItem value="MEDIUM_WIDTH">Medium</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>

                  <Box mt={3}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <FaList /> Cards ({templateData.cards.length})
                      </Typography>
                      {templateData.richCardType === "carousel" &&
                        templateData.cards.length < 10 && (
                          <Button
                            variant="outlined"
                            startIcon={<FaPlus />}
                            onClick={addNewCard}
                            size="small"
                          >
                            Add Card
                          </Button>
                        )}
                    </Box>

                    {templateData.richCardType === "carousel" && (
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Select Card to Edit</InputLabel>
                        <Select
                          label="Select Card to Edit"
                          value={selectedCardIndex}
                          onChange={(e) =>
                            setSelectedCardIndex(Number(e.target.value))
                          }
                        >
                          {templateData.cards.map((card, i) => (
                            <MenuItem key={card.id} value={i}>
                              Card {i + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <CardEditor
                      index={
                        templateData.richCardType === "standalone"
                          ? 0
                          : selectedCardIndex
                      }
                      templateData={templateData}
                      setTemplateData={setTemplateData}
                      richCardType={templateData.richCardType}
                      onRemove={
                        templateData.richCardType === "carousel"
                          ? handleRemoveCard
                          : undefined
                      }
                    />

                    {templateData.richCardType === "carousel" && (
                      <Box mt={3}>
                        <SuggestionsEditor
                          suggestions={templateData.outerSuggestions}
                          onChange={handleOuterSuggestionsChange}
                          maxSuggestions={2}
                          title="Carousel Suggestions"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </SectionPaper>

            {!templateData.richCardType && (
              <SectionPaper
                title="Suggested Actions"
                icon={<FaMousePointer color={theme.palette.primary.main} />}
                defaultExpanded={false}
              >
                <SuggestionsEditor
                  suggestions={templateData.buttons}
                  onChange={(newButtons) =>
                    handleInputChange("buttons", newButtons)
                  }
                  maxSuggestions={2}
                  title="Message Suggestions"
                />
              </SectionPaper>
            )}
          </Grid>

          <Grid item xs={12} lg={4}>
            <TemplatePreview templateData={templateData} />
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button variant="outlined" onClick={() => {}}>
            Save as Draft
          </Button>

          <Tooltip title="Create template">
            <GlassButtonLoading
              onClick={handleSumitTemplate}
              startIcon={<FaEdit />}
              variant="contained"
              size="large"
              sx={{ px: 4 }}
            >
              Create Template
            </GlassButtonLoading>
          </Tooltip>
        </Box>
      </Box>
    </Container>
  );
};

export default RcsAddTemplate;
